import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ButtonTypeEnum } from '@panaya/ui/p-button';
import { IconName } from '@panaya/ui/p-icon';
import { LinkTarget } from '@panaya/ui/p-link';
import { ColorName } from '@panaya/ui/styles-to-code';
import { KeyboardService } from '../../keyboard';
import { PFieldAbstractComponent } from '../p-field-abstract';

@Component({
    selector: 'p-field-link',
    template: `
        <p-field
            [isMandatory]="isMandatory"
            [label]="label"
            [labelTooltip]="labelTooltip"
            [customClasses]="customClasses"
            [isDisable]="isDisabled"
            [isReadOnly]="isReadOnly"
            [shouldTransformLabelTextCase]="shouldTransformLabelTextCase"
            [preLabelIcon]="preLabelIcon"
            [preLabelIconColor]="preLabelIconColor"
        >
            <div slot="editMode" fxFlex fxLayout="start center" fxLayoutGap="10px">
                <mat-form-field
                    class="p-input-text p-input-link p-input-text-readonly"
                    appearance="fill"
                    floatLabel="always"
                    *ngIf="!hasValue"
                >
                    <p-button
                        *ngIf="isBefore && isShowIcon"
                        class="p-input-link-prefix"
                        matPrefix
                        [buttonType]="buttonType"
                        iconBefore="link"
                        iconBeforeSize="sm"
                        (buttonClicked)="onEdit()"
                    ></p-button>
                    <input
                        matInput
                        [attr.automation-id]="'p-field-link_' + label"
                        type="text"
                        [placeholder]="placeholder | sentenceCase"
                        [readonly]="true"
                        [value]="getUrlText(value)"
                        (click)="onEdit()"
                        class="p-input-text-input"
                        [matTooltip]="getUrlText(value)"
                        [showOnlyWhenEllipsis]="true"
                        matTooltipPosition="above"
                        tabindex="-1"
                    />
                    <p-button
                        *ngIf="!isBefore && isShowIcon"
                        style="position: absolute;"
                        [buttonType]="buttonType"
                        iconAfter="link"
                        iconAfterSize="sm"
                        (buttonClicked)="onEdit()"
                    ></p-button>
                </mat-form-field>
                <mat-form-field
                    class="p-input-text p-input-link p-input-text-readonly"
                    appearance="fill"
                    floatLabel="always"
                    *ngIf="hasValue"
                >
                    <p-link [url]="getUrlHref(value)" [target]="target">
                        <input
                            matInput
                            [attr.automation-id]="'p-field-link_' + label"
                            type="text"
                            [placeholder]="placeholder | sentenceCase"
                            [readonly]="true"
                            [value]="getUrlText(value)"
                            class="p-input-text-input"
                            [matTooltip]="getUrlText(value)"
                            [showOnlyWhenEllipsis]="true"
                            matTooltipPosition="above"
                            tabindex="-1"
                        />
                    </p-link>
                    <p-button
                        class="p-input-text-icon edit-link"
                        matSuffix
                        ariaLabel="Edit Link"
                        [buttonType]="buttonType"
                        iconBefore="link"
                        iconBeforeSize="sm"
                        (buttonClicked)="onEdit()"
                        matTooltip="{{ linkTooltip || 'Actions.editLink' | translate }}"
                        matTooltipPosition="above"
                    ></p-button>
                    <p-button
                        class="p-input-text-icon remove-link"
                        matSuffix
                        ariaLabel="Unlink"
                        [buttonType]="buttonType"
                        iconBefore="unlink"
                        iconBeforeSize="sm"
                        (buttonClicked)="onUnlink()"
                        matTooltip="{{ unlinkTooltip || 'Actions.unlink' | translate }}"
                        matTooltipPosition="above"
                    ></p-button>

                    <mat-error *ngIf="!isValid" class="p-input-text-error">
                        {{ errorMessage }}
                    </mat-error>
                </mat-form-field>
            </div>
            <div slot="readMode">
                <mat-form-field
                    *ngIf="getUrlText(value)"
                    class="p-input-text p-input-link"
                    appearance="fill"
                    [class.p-input-link-readonly]="isReadOnly"
                    floatLabel="always"
                >
                    <input
                        matInput
                        [attr.automation-id]="'p-field-link_' + label"
                        readonly
                        [value]="getUrlText(value)"
                        [attr.aria-label]="ariaLabel"
                        (click)="openLink()"
                        [matTooltip]="getUrlText(value)"
                        [showOnlyWhenEllipsis]="true"
                        matTooltipPosition="above"
                        class="p-input-text-input"
                    />
                </mat-form-field>
            </div>
        </p-field>
    `,
    styleUrls: ['./p-field-link.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldLinkComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldLink'
})
export class PFieldLinkComponent extends PFieldAbstractComponent<Link> implements OnInit {
    buttonType = ButtonTypeEnum.IsIcon;

    @Input() label?: string;
    @Input() placeholder?: string;
    @Input() errorMessage?: string;
    @Input() isBefore: boolean = true;
    @Input() isShowIcon: boolean = true;
    @Input() isMandatory = false;
    @Input() target?: LinkTarget;
    @Input() customClasses?: string;
    @Input() linkTooltip?: string;
    @Input() unlinkTooltip?: string;
    @Input() preLabelIcon?: IconName;
    @Input() preLabelIconColor!: ColorName;

    @Output() linkClick = new EventEmitter<any>();
    @Output() edit = new EventEmitter<{ text: string | undefined; href: string | undefined }>();
    @Output() unlink = new EventEmitter();

    constructor(cd: ChangeDetectorRef, private translate: TranslateService, private keyboardService: KeyboardService) {
        super(cd);
    }

    ngOnInit(): void {
        this.placeholder = this.placeholder || this.translate.instant('Actions.addLink');
    }

    get hasValue() {
        return this.value?.text;
    }

    protected getUrlText(urlObj: Link | undefined): string {
        return urlObj?.text || '';
    }

    protected getUrlHref(urlObj: Link | undefined): string | undefined {
        return urlObj?.href;
    }

    updateValue(text: string, href: string) {
        this.value = { text, href };
        this._onChange(this.value);
        this.cd.detectChanges();
    }

    onEdit() {
        const { text, href } = this.value || {};
        this.edit.emit({ text, href });
    }

    onUnlink() {
        this.value = { text: '', href: '' };
        this._onChange(this.value);
        this.cd.detectChanges();
        this.unlink.emit();
    }

    openLink() {
        const { text = '', href = '' } = this.value || {};
        this.linkClick.emit({ text, href });
    }

    onKeydownEvent(e: KeyboardEvent) {
        const code = e.keyCode;

        if (this.keyboardService.isDown(code) || this.keyboardService.isEnter(code)) {
            this.onEdit();
        }
    }
}

interface Link {
    text: string;
    href: string;
    valueTooltip?: string;
}
